/**
 * English locale strings for SeedGovernance.
 * All UI strings extracted here — no hardcoded text in components.
 * Architecture-ready for future ar.ts (Arabic/RTL).
 */
export const en = {
    // Brand
    brand: {
        name: 'SeedGovernance',
        tagline: 'Data Governance Made Simple for Growing Companies',
        description: 'The compliance platform built for startups and growing companies. 33 frameworks, 688 templates, 3 regions.',
    },
    // Navigation
    nav: {
        home: 'Home',
        features: 'Features',
        frameworks: 'Frameworks',
        pricing: 'Pricing',
        consultants: 'For Consultants',
        blog: 'Blog',
        docs: 'Docs',
        security: 'Security',
        about: 'About',
        contact: 'Contact',
        login: 'Log In',
        signup: 'Start Free Trial',
        assessment: 'Free Assessment',
    },
    // Home page
    home: {
        heroTitle: 'Data Governance Made Simple for Growing Companies',
        heroSubtitle: '33 compliance frameworks. 688 ready-to-use templates. Choose your region. Start in minutes.',
        ctaPrimary: 'Take the Free Assessment',
        ctaSecondary: 'Start Free Trial',
        assessmentTeaserTitle: 'Not sure which frameworks apply to you?',
        assessmentTeaserDesc: 'Answer 12 quick questions and we\'ll tell you exactly which regulations matter for your business — and recommend the right plan.',
        assessmentTeaserCta: 'Take the 2-Minute Assessment',
        frameworksTitle: 'Built for 33 Compliance Frameworks',
        frameworksSubtitle: 'From GDPR to PDPL, HIPAA to ISO 27001 — every framework your business needs, in one platform.',
        frameworksMore: 'and {count} more frameworks',
        regionalTitle: 'Your Data Stays Where Your Regulators Require',
        regionalSubtitle: 'Choose your region at signup — we handle the rest. All data stored in-region for full compliance.',
        investorTitle: 'Investor-Ready Compliance',
        investorSubtitle: 'Only completed, verified compliance items appear in your exports and auditor views — no half-baked documents.',
        collaborativeTitle: 'Your Team, Your Consultant, Your Control',
        collaborativeSubtitle: 'Invite your consultant. They edit, you stay in control. Assign stewards to specific domains. Full RACI visibility.',
        socialProofTemplates: '688 Templates',
        socialProofFrameworks: '33 Frameworks',
        socialProofModules: '13 Modules',
        socialProofRegions: '3 Regions',
        ctaBottomTitle: 'Ready to simplify your compliance?',
    },
    // Pricing
    pricing: {
        title: 'Simple, Transparent Pricing',
        subtitle: 'Start with a free assessment. Upgrade when you\'re ready. No credit card required for trial.',
        monthly: 'Monthly',
        annual: 'Annual',
        annualSave: 'Save 20%',
        perMonth: '/month',
        billedAnnually: 'billed annually',
        custom: 'Custom',
        frameworks: 'frameworks',
        users: 'users',
        storage: 'storage',
        freeTrial: '14-day free trial',
        noCreditCard: 'No credit card required',
        notSureBanner: 'Not sure which plan? Take the free assessment to find out.',
        consultantCallout: 'Working with a consultant? Pro plan and above lets you invite them with full edit access.',
        featuresIncluded: 'Everything in {tier}, plus:',
    },
    // Framework categories
    categories: {
        privacy: 'Privacy & Data Protection',
        healthcare: 'Healthcare & Life Sciences',
        security: 'Information Security',
        governance: 'IT Governance & Risk',
        ai: 'AI & Emerging',
        assessment: 'Industry Assessments',
    },
    // Roles
    roles: {
        platform_admin: 'Platform Admin',
        owner: 'Owner',
        admin: 'Admin',
        consultant: 'Consultant',
        steward: 'Steward',
        viewer: 'Viewer',
        auditor: 'Auditor',
    },
    // Status labels
    status: {
        notStarted: 'Not Started',
        inProgress: 'In Progress',
        done: 'Done',
        blocked: 'Blocked',
        locked: 'Locked',
        unlockable: 'In Progress',
        exportable: 'Ready to Export',
    },
    // Regions
    regions: {
        ksa: 'Saudi Arabia / GCC',
        eu: 'European Union / UK',
        us: 'United States',
    },
    // Assessment
    assessment: {
        title: 'Quick Compliance Assessment',
        subtitle: 'Answer 12 questions to find out which frameworks apply to your business.',
        startButton: 'Start Assessment',
        nextButton: 'Next',
        previousButton: 'Back',
        submitButton: 'See My Results',
        resultsTitle: 'Your Compliance Profile',
        mandatory: 'Mandatory',
        recommended: 'Recommended',
        optional: 'Optional',
        recommendedTier: 'Recommended Plan',
        recommendedRegion: 'Recommended Region',
        emailCapture: 'Enter your email to see your results',
        emailPlaceholder: 'work@company.com',
        companyPlaceholder: 'Company name',
        downloadPdf: 'Download Assessment Summary (PDF)',
        startTrial: 'Start your {tier} trial',
    },
    // Consultants page
    consultants: {
        heroTitle: 'Help Your Clients Get Governance Right',
        heroSubtitle: 'Your clients own their accounts. You\'re invited in as a trusted advisor — they stay in control.',
        howItWorksTitle: 'How It Works',
        step1: 'Client creates their SeedGovernance account',
        step2: 'Client invites you with the Consultant role',
        step3: 'You edit deliverables, run assessments, upload evidence',
        step4: 'Create auditor links for their investors and auditors',
        workspaceSwitcher: 'Work with multiple clients? See all your accounts in one workspace switcher.',
        ctaPrimary: 'Tell Your Clients About SeedGovernance',
        ctaSecondary: 'Start a Free Trial for Your Own Company',
    },
    // Footer
    footer: {
        product: 'Product',
        company: 'Company',
        legal: 'Legal',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
        cookiePolicy: 'Cookie Policy',
        dpa: 'Data Processing Agreement',
        subProcessors: 'Sub-Processors',
        copyright: '© {year} SeedGovernance. All rights reserved.',
    },
    // Cookie consent
    cookies: {
        title: 'We respect your privacy',
        description: 'We use cookies for analytics to improve our site. No cookies are set before your consent.',
        acceptAll: 'Accept All',
        rejectAll: 'Reject All',
        managePreferences: 'Manage Preferences',
    },
    // Common
    common: {
        learnMore: 'Learn More',
        getStarted: 'Get Started',
        viewAll: 'View All',
        close: 'Close',
        loading: 'Loading...',
        error: 'Something went wrong',
        yes: 'Yes',
        no: 'No',
        notSure: 'Not sure',
    },
};
//# sourceMappingURL=en.js.map